import Segment from '@sportnet/ui/Segment';
import SegmentHeader from '@sportnet/ui/Segment/Header';
import SportnetUiAppContext from '@sportnet/ui/TheLayout/AppContext';
import Button from '@sportnet/ui/Button';
import FormField from '@sportnet/ui/FormField';
import Col, { Row } from '@sportnet/ui/Grid';
import React, { useState, useEffect } from 'react';
import Api from '../../Api';
import Layout from '../../components/Layout';
import { LoaderContainer } from '../../components/List';
import Loader from '@sportnet/ui/Loader';
import { __ } from '../../utilities';
import getApiError from '../../utilities/getApiError';
import { useAuth } from '@sportnet/auth-react';

// const MOCK_CONNECTIONS = [
//   {
//     type: 'omega',
//     name: 'Omega',
//     status: 'active',
//     payload: { apikey: 'sk_live_abc123def456' },
//   },
//   {
//     type: 'kalendar',
//     name: 'Kalendár',
//     status: 'inactive',
//     payload: {},
//   },
//   {
//     type: 'esport',
//     name: 'eSport',
//     status: 'inactive',
//     payload: {},
//   },
//   {
//     type: 'ticketing',
//     name: 'Ticketing',
//     status: 'active',
//     payload: { apikey: 'tk_prod_xyz789' },
//   },
// ];

const ConnectionsList = () => {
  const { ppo: appSpace } = useAuth();
  const [connections, setConnections] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [apiKeys, setApiKeys] = useState({});
  const [savingType, setSavingType] = useState(null);

  const fetchConnections = React.useCallback(async () => {
    setIsFetching(true);
    try {
      const res = await Api.crmMyOrganizationConnections(appSpace);
      setConnections(res.items || []);
    } catch (e) {
      const err = getApiError(e);
      alert(
        `${__('Chyba načítania prepojení')}: ${err.details.statusCode} ${err.details.name} ${err.message}`,
      );
    } finally {
      setIsFetching(false);
    }
  }, [appSpace]);

  useEffect(() => {
    if (appSpace) {
      fetchConnections();
    }
  }, [appSpace, fetchConnections]);

  const handleConnect = async type => {
    const apikey = apiKeys[type];
    if (!apikey) {
      alert(__('Zadajte API kľúč'));
      return;
    }
    setSavingType(type);
    try {
      await Api.crmMyOrganizationConnectionCreate(appSpace, type, {
        data: { apikey },
      });
      setApiKeys(prev => {
        const next = { ...prev };
        delete next[type];
        return next;
      });
      await fetchConnections();
    } catch (e) {
      const err = getApiError(e);
      alert(
        `${__('Chyba pripojenia')}: ${err.details.userinfo || err.details.description || err.message}`,
      );
    } finally {
      setSavingType(null);
    }
  };

  const handleDisconnect = async type => {
    if (!window.confirm(__('Naozaj chcete odpojiť toto prepojenie?'))) {
      return;
    }
    setSavingType(type);
    try {
      await Api.crmMyOrganizationConnectionDelete(appSpace, type);
      await fetchConnections();
    } catch (e) {
      const err = getApiError(e);
      alert(
        `${__('Chyba odpojenia')}: ${err.details.userinfo || err.details.description || err.message}`,
      );
    } finally {
      setSavingType(null);
    }
  };

  return (
    <Layout>
      <SportnetUiAppContext title={__('Prepojenia')} />
      {isFetching ? (
        <LoaderContainer>
          <Loader size="xl" />
        </LoaderContainer>
      ) : (
        <Segment>
          <Row>
            {connections.map(connection => (
              <Col key={connection.type} xs={12} s={6} m={6} l={4}>
                <Segment
                  raised
                  header={
                    <SegmentHeader size="s">
                      {connection.name || connection.type}
                    </SegmentHeader>
                  }
                  loading={savingType === connection.type}
                >
                  {connection.status === 'active' ? (
                    <>
                      <FormField
                        label={__('API kľúč')}
                        value={
                          connection.payload && connection.payload.apikey
                            ? connection.payload.apikey
                            : ''
                        }
                        readOnly
                      />
                      <div style={{ marginTop: 16 }}>
                        <Button
                          danger
                          onClick={() => handleDisconnect(connection.type)}
                          disabled={savingType === connection.type}
                        >
                          {__('Odpojiť')}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <FormField
                        label={__('API kľúč')}
                        value={apiKeys[connection.type] || ''}
                        onChange={e => {
                          const val = e.target.value;
                          setApiKeys(prev => ({
                            ...prev,
                            [connection.type]: val,
                          }));
                        }}
                        placeholder={__('Zadajte API kľúč')}
                      />
                      <div style={{ marginTop: 16 }}>
                        <Button
                          primary
                          onClick={() => handleConnect(connection.type)}
                          disabled={
                            savingType === connection.type ||
                            !apiKeys[connection.type]
                          }
                        >
                          {__('Pripojiť')}
                        </Button>
                      </div>
                    </>
                  )}
                </Segment>
              </Col>
            ))}
          </Row>
        </Segment>
      )}
    </Layout>
  );
};

export default ConnectionsList;
